/**
 * Stale-chunk recovery (BUG-139 / BUG-145).
 *
 * After a deploy, an already-loaded SPA bundle still references the previous
 * build's content-hashed chunk filenames. Navigating to a `lazy()` route then
 * requests a chunk the new deploy deleted → import 404. React's Suspense
 * catches that rejection and routes it to the nearest error boundary, so the
 * global window handlers in main.tsx never see it. Every error boundary must
 * call this too, or the user lands on a "Session Interrupted" screen whose
 * retry just reloads into the same stale reference.
 *
 * Single source of truth for chunk-error detection + recovery. Used by
 * main.tsx (window handlers), ErrorBoundary, and AuthErrorBoundary.
 */

const CHUNK_ERROR_PATTERNS = [
  'dynamically imported module',
  'Importing a module script',
  'Failed to fetch',
  'Loading chunk',
  'ChunkLoadError',
];

export function isChunkLoadError(message: string | undefined | null): boolean {
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/**
 * Reload to fetch fresh assets, with a loop guard. The first two attempts do a
 * normal reload — the origin serves index.html as `no-store`, so the browser
 * picks up the new chunk hashes. After two failed attempts, force a
 * cache-busting query to defeat any CDN/edge-cached HTML.
 */
export function recoverFromChunkLoad(): void {
  const reloadCount = parseInt(sessionStorage.getItem('chunkReloadCount') || '0', 10);
  if (reloadCount < 2) {
    sessionStorage.setItem('chunkReloadCount', String(reloadCount + 1));
    window.location.reload();
  } else {
    sessionStorage.removeItem('chunkReloadCount');
    window.location.href = window.location.pathname + '?_bust=' + Date.now();
  }
}

/** Clear the reload guard once assets have loaded successfully. */
export function clearChunkReloadGuard(): void {
  sessionStorage.removeItem('chunkReloadCount');
}
